<template>
  <div>
    <ValidationObserver
      ref="fileObserver" >
      <ValidationProvider
        v-slot="{ errors }"
        :name="schema.title"
        :rules="validators">
        <b-button
          :disabled="scrapingInProgress"
          class="url-button flex flex-align-center icon"
          :style="{ 'background-image': scrapingInProgress ? 'unset' : schema.addButtonIcon ? `url(${schema.addButtonIcon})` : '' }"
          @click="showUrlsModal">
          <span
            v-if="scrapingInProgress"
            style="padding-top: 2px; margin-left: -20px;"
            class="flex flex-align-center" >
            <CircleSpinner
              :diameter="14"
              infinite
              :stroke-width="2"
              background="#CFD8DC" />
            <p class="action-text dark ml-2">Web scraping in progress...</p>
          </span>
          <span
            v-else
            class="ellipsis greedy"
            :class="{ subtle: buttonText2 === schema.addButtonText }" >{{ buttonText2 }}</span>
          <div
            v-if="!scrapingInProgress && filterCounter && buttonText2 !== schema.addButtonText"
            class="filter-btn ml-1 flex center-all" >
            <SBIcon
              class="mal-1"
              :icon="SBIconList.FILTER"
              :color="SBColors.blackUltralight"
              :size="10" />
            <span class="p-0 m-0">{{ filterCounter }}</span>
          </div>
        </b-button>
        <!-- SUBTITLE -->
        <div
          v-if="scrapingInProgress"
          class="subtitle flex flex-align-center show-hand"
          @click="cancelScrape" >
          <SBIcon
            class="mr-1"
            :icon="SBIconList.CLOSE_THIN"
            :color="SBColors.redPrimary"
            :size="10" />
          <span class="subtle error">Cancel</span>
        </div>
        <div
          v-else-if="previousScrape && !errors[0]"
          class="subtitle flex flex-align-center" >
          <div
            v-if="!hasChanges"
            class="flex flex-align-center mr-1 show-hand rescrape-btn"
            @click="reScrape">
            <SBIcon
              class="mr-1"
              :icon="SBIconList.RECYCLE"
              :color="SBColors.bluePrimary"
              :size="10" />
            <span
              class="blue">Rescrape</span>
          </div>
          <span class="subtle ellipsis">(last scrape: {{ previousScrape | dateRegionDMYYY }} at {{ previousScrape | subMessageDate }})</span>
        </div>
        <div
          v-else-if="schema.subtitle && !errors[0]"
          class="subtitle flex flex-align-center">
          <svg
            v-if="schema.subtitlePrefixIcon && typeof schema.subtitlePrefixIcon === 'string' && schema.subtitlePrefixIcon.endsWith('.svg')"
            class="mr-1 flex-start-image"
            :data-src="schema.subtitlePrefixIcon"
            fill="currentColor"
            width="10"
            height="10" />
          <img
            v-else-if="schema.subtitlePrefixIcon && typeof schema.subtitlePrefixIcon === 'string'"
            :src="schema.subtitlePrefixIcon"
            style="width: 10px; height: 10px;"
            class="mr-1 flex-start-image"
          >
          <SBIcon
            v-else-if="!schema.noSubtitlePrefixIcon"
            class="mr-1"
            :icon="SBIconList.INFO"
            :color="SBColors.greyLight"
            :size="10" />
          <span v-html="schema.subtitle" />
        </div>
      </ValidationProvider>
    </ValidationObserver>

    <FormSchemaModal
      ref="formSchemaModalRef"
      :key="updateModalKey"
      v-model="urls"
      :data="updatedModalSchema || schema.modal"
      @initialized="modalInitialized"
      @closed="modalClosed" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import CircleSpinner from '@/components/animatedIcons/CircleSpinner';
import api from '@/helpers/api';
import randomString from '@/helpers/randomString';
import { ModalSize, ModalVariant } from '@/helpers/enums/modal.enum';
import FormSchemaModal from './FormSchema.modal';
import StatusTypes from '../../../helpers/enums/statusTypes.enum';

export default {
  name: 'UrlInputWrapper',
  components: {
    FormSchemaModal,
    CircleSpinner,
    SBIcon,
  },
  props: {
    validators: {
      type: String,
      default: '',
    },
    schema: {
      type: Object,
      default: () => ({}),
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    propName: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      urls: {},
      urlsBackup: {},
      dirty: false,
      previousScrape: null,
      hasChanges: false,
      updatedModalSchema: null,
      updateModalKey: 0,
      preventEmit: false,
      SBColors,
      SBIconList,
    };
  },
  computed: {
    ...mapGetters({
      backgroundActions: 'automation/backgroundActions',
      currentOrganisation: 'organisation/currentOrganisation',
      updatedAISchema: 'automation/updatedAISchema',
    }),
    assistantId() {
      return this.$route.params.assistantId;
    },
    scrapingInProgress() {
      return this.schema.scrapeFinished === false || this.backgroundActions.filter(ba => ba.assistantId === this.assistantId && ba.action === 'webScrape' && [StatusTypes.RUNNING, StatusTypes.PENDING].includes(ba.status)).length > 0;
    },
    urlCount() {
      if (!this.urls.url || !this.urls.url.data || !this.urls.url.data.value) {
        return 0;
      }
      return this.urls.url.data.value.split(',').length;
    },
    buttonText() {
      if (!this.urlCount) {
        return this.schema.addButtonText;
      }
      if (this.urlCount === 1) {
        return this.urls.url.data.value;
      }
      return `${this.urlCount} pages`;
    },
    buttonText2() {
      // console.group('UrlInputWrapper');
      const targetKey = Object.keys(this.urls).find(k => this.schema.populatedText.includes(k));
      const targetElement = this.urls[targetKey];
      const targetCount = this.schema.count[targetKey] || 0;
      const removeCount = Object.keys(this.urls).reduce((acc, curr) => (this.urls[curr].removeCount || 0) + acc, 0);
      let text;
      const regex = new RegExp(/^http(s)*:\/\//, 'g');
      if (targetElement && targetElement.data && Array.isArray(targetElement.data) && targetElement.data.filter(v => v).length) {
        text = targetElement.data.length === 1 ? targetElement.data[0] : `${targetElement.data.length + targetCount - removeCount} links`;
        return text.replace(regex, '');
      }
      if (targetElement && targetElement.data && targetElement.data.value && Array.isArray(targetElement.data.value) && targetElement.data.value.filter(v => v).length) {
        text = targetElement.data.value.length === 1 ? targetElement.data.value[0] : `${targetElement.data.value.length + targetCount - removeCount} links`;
        return text.replace(regex, '');
      }
      if (targetElement && targetElement.data && targetElement.data.value && !Array.isArray(targetElement.data.value)) {
        text = targetElement.data.value;
        return text.replace(regex, '');
      }
      if (targetCount > 1) {
        text = `${targetCount - removeCount} links`;
        return text.replace(regex, '');
      }
      return this.schema.addButtonText;
    },
    filterCounter() {
      // const targetKey = Object.keys(this.urls).find(k => this.schema.populatedText.includes(k));
      // const targetElement = this.urls[targetKey];
      // if (targetElement && targetElement.data && Array.isArray(targetElement.data)) {
      //   return targetElement.data.length;
      // }
      // return 0;

      let counter = 0;
      Object.keys(this.urls).forEach((key) => {
        if (this.schema.countFields.includes(key) && this.urls[key].data.value.filter(d => d).length) {
          counter++;
        }
      });

      return counter;
    },
  },
  watch: {
    backgroundActions() {
      const targetScrape = this.backgroundActions.filter(ba => ba.assistantId === this.assistantId && ba.action === 'webScrape' && [StatusTypes.COMPLETED].includes(ba.status));
      if (targetScrape && targetScrape[0] && targetScrape[0].status === StatusTypes.COMPLETED && targetScrape[0].finished) {
        this.previousScrape = Math.floor(Date.now() / 1000);
      }
    },
    urls: {
      handler() {
        this.emitData();
      },
      immediate: true,
    },
    /**
     * updatedAISchema is updated every time form submits and the new schema is saved so each element has to check if the new data is available for it and needs to update.
     */
    updatedAISchema() {
      const targetValue = this.getObjectCopyByKey(this.updatedAISchema, this.propName);
      if (targetValue) {
        this.updatedModalSchema = JSON.parse(JSON.stringify(targetValue.modal));
        this.preventEmit = true;
        this.urls = {};
        this.urlsBackup = {};
        this.$nextTick(() => {
          this.preventEmit = false;
          this.updateModalKey++;
        });
      }
    },
  },
  mounted() {
    if (this.schema.value) {
      // Set files on edit
    }
    if (this.schema.stepID) {
      this.$eventBus.$on(`validate-${this.schema.stepID}`, () => {
        if (!this.$refs.fileObserver) return;
        this.$refs.fileObserver.validate();
      });
    }
    if (this.schema.scrapeFinished === false) {
      this.addBackgroundActions([{
        id: randomString(10),
        action: 'webScrape',
        assistantId: this.assistantId,
        status: StatusTypes.RUNNING,
      }]);
    } else if (this.schema.scrapeFinished !== null) {
      this.previousScrape = this.schema.scrapeFinished;
    }
    setTimeout(() => {
      this.emitData(true);
      if (this.schema.value) {
        if (!this.$refs.fileObserver) return;
        this.$refs.fileObserver.validate();
      }
    }, 0);
  },
  methods: {
    ...mapActions({
      addBackgroundActions: 'automation/addBackgroundActions',
      backgroundActionStatusChange: 'automation/backgroundActionStatusChange',
    }),
    cancelScrape() {
      this.$numberInfoModal.show({
        size: ModalSize.MEDIUM,
        title: 'Cancel web scraping',
        message: 'Please confirm whether you want to cancel this web scrape. All links that have already been scraped will be stored in the knowledge base.',
        confirmButtonText: 'Yes, cancel',
        cancelButtonText: 'No, don\'t cancel',
        variant: ModalVariant.DANGER,
        confirmCallback: () => {
          api('PUT', `organisation/${this.currentOrganisation.id}/assistant/${this.assistantId}/links/cancel-scrape`, null, null, 'w')
            .then((response) => {
              if (response.status === 200) {
                this.backgroundActionStatusChange({
                  action: 'webScrape',
                  assistantId: this.assistantId,
                  status: StatusTypes.CANCELED,
                });
              }
            });
        },
      });
    },
    reScrape() {
      this.$numberInfoModal.show({
        size: ModalSize.MEDIUM,
        title: 'Start rescrape',
        message: 'Please confirm if you want to rescrape or recrawl the existing website and pages. AI assistant will use existing data until new data is scraped.',
        confirmButtonText: 'Yes, continue',
        confirmCallback: () => {
          api('PUT', `organisation/${this.currentOrganisation.id}/assistant/${this.assistantId}/links/rescrape`, null, null, 'w')
            .then((response) => {
              if (response.status === 200) {
                this.addBackgroundActions([{
                  action: 'webScrape',
                  assistantId: this.assistantId,
                  id: randomString(10),
                  status: StatusTypes.PENDING,
                }]);
                this.$eventBus.$emit('editMessage', {
                  key: 'urlInput',
                  message: 'Rescrape initiated',
                });
              }
            });
        },
      });
    },
    modalClosed() {
      this.dirty = true;
      this.emitData();
    },
    modalInitialized() {
      this.urlsBackup = JSON.parse(JSON.stringify(this.urls));
      // window.eventBus.$emit('reset-changes');
      this.$eventBus.$emit('resetInitialData');
    },
    showUrlsModal() {
      this.urlsBackup = JSON.parse(JSON.stringify(this.urls));
      this.$refs.formSchemaModalRef.show(this.urls);
    },
    /**
     * Method will cleanup the formSchema values and emit the data for each modal field.
     * @param urls formSchema data values from modal
     */
    getUrlsValues(urls) {
      const values = {};
      Object.keys(urls).forEach((key) => {
        if (urls[key].data && urls[key].data.value) {
          values[key] = urls[key].data.value;
          if (Array.isArray(values[key])) {
            values[key] = values[key].filter(v => v);
          }
        } else if (!urls[key].data) {
          values[key] = this.getUrlsValues(urls[key]);
        }
      });
      return values;
    },
    /**
     * Method will emit value in a standard format as other components used in combination with formSchema which is data/value but it will also include the formSchema value from modal formSchema to enable complete access to the separate modal formSchema.
     */
    emitData(reset = false) {
      if (!this.$refs.fileObserver || this.preventEmit) return;
      this.$refs.fileObserver.validate({ silent: true })
        .then((valid) => {
          const hasData = this.buttonText2 !== this.schema.addButtonText;
          this.$emit('input', {
            valid: this.buttonText2 !== this.schema.addButtonText,
            name: this.schema.returnKey || '',
            data: {
              ...this.urls,
              value: this.getUrlsValues(this.urls),
            },
            // postAction: 'webScrape',
            dirty: this.dirty,
            validOneOf: this.schema.validOneOf,
            validOneOfMessage: this.schema.validOneOfMessage,
            hasData, // Indicates that the field has data regardles of it being valid or not. This is used for the form validation where at least one field is required even thou they are not required individually.
          });
          this.hasChanges = !this.$lodash.isEqual(JSON.parse(JSON.stringify(this.urlsBackup)), JSON.parse(JSON.stringify(this.urls)));
          if (this.hasChanges && hasData && this.schema.editMessage && this.schema.editMessage.default) {
            // Edit message will be triggered only if there are changes on the data and if there are data. Both should be checked because if save is clicked without changes then the empty parameters are set and it is being compared to an empty object resulting with a false positive data available.
            this.$eventBus.$emit('editMessage', {
              key: 'urlInput',
              message: this.schema.editMessage.default,
            });
          } else if (this.hasChanges && !hasData && this.schema.editMessage && this.schema.editMessage.empty) {
            this.$eventBus.$emit('editMessage', {
              key: 'urlInput',
              message: this.schema.editMessage.empty,
            });
          } else {
            this.$eventBus.$emit('clearEditMessage');
          }
        })
        .finally(() => {
          if (reset) this.$refs.fileObserver.reset();
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.flex-start-image {
  top: 1px;
  position: relative;
}

.url-button {
  border-radius: 10px;
  width: 100%;
  height: 34px;
  padding-top: 5px !important;
  background-image: url('~@/assets/img/SB-Icons/add-urls.svg');
  text-align: left;

  &.disabled {
    opacity: 1 !important;
  }

  .subtle {
    color: #9eaeb5;
    font-weight: 400;
  }

  &.icon {
    background-size: 14px 14px;
    background-repeat: no-repeat;
    background-position-x: 12px;
    background-position-y: center;

    span {
      padding-left: 16px;
    }
  }

  &:hover {
    .subtle {
      color: $blackPrimary;
    }
  }

  .filter-btn {
    margin-top: 4px;
    margin-right: -4px;
    color: $blackUltralight;
    span {
      font-size: 12px;
    }
  }
}

.rescrape-btn:hover {
  * {
    color: $blackPrimary !important;
  }

  ::v-deep .icon.mask {
    background-color: $blackPrimary !important;
  }
}
</style>
